import { createSlice } from "@reduxjs/toolkit"

import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list.ts"
import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import { getResponseMessage } from "../../../utils/handlers/get-response-message"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import {
  createCustomer,
  deleteCustomer,
  fetchCustomerDetails,
  fetchCustomers,
  fetchCustomersFilterOptions,
  inviteCustomer,
  reactivateCustomer,
  resendInvite,
  suspendCustomer,
  updateCustomer
} from "./customers.thunks.ts"
import { type CustomersState } from "./customers.types.ts"

// Initial State
const initialState: CustomersState = {
  fetching: false,
  requestId: null,
  result: {
    count: 0,
    next: null,
    results: []
  },

  filters: {
    q: null,
    status: null,
    created_after: null,
    created_before: null
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: []
  },

  saveFetching: false,
  saveResult: null,

  customerDetailsFetching: false,
  customerDetails: null
}

// Slice
const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    customersSetProp: generateSetPropReducer<CustomersState>(),
    clearCustomersFilters: (state) => {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchCustomersFilterOptions
      .addCase(fetchCustomersFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(fetchCustomersFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = { ...state.filterOptions, ...action.payload }
      })
      .addCase(fetchCustomersFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      // fetchCustomers
      .addCase(fetchCustomers.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
        state.result = beforeFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFetch(
          state.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFailedFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })

      // createCustomer
      .addCase(createCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_create",
          message: getResponseMessage(
            action.payload,
            "Customer created successfully"
          )
        }
      })
      .addCase(createCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      // updateCustomer
      .addCase(updateCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_update",
          message: getResponseMessage(
            action.payload,
            "Customer saved successfully"
          )
        }
      })
      .addCase(updateCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      // deleteCustomer
      .addCase(deleteCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        const newResults = state.result.results.filter(
          (customer) => customer.id !== action.payload?.id
        )

        state.saveFetching = false
        state.saveResult = {
          type: "customer_delete",
          message: getResponseMessage(
            action.payload,
            "Customer was removed successfully"
          )
        }
        state.result = {
          ...state.result,
          count: state.result.count - 1,
          results: newResults
        }
      })
      .addCase(deleteCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchCustomerDetails
      .addCase(fetchCustomerDetails.pending, (state) => {
        state.customerDetailsFetching = true
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.customerDetailsFetching = false
        state.customerDetails = action.payload
      })
      .addCase(fetchCustomerDetails.rejected, (state) => {
        state.customerDetailsFetching = false
      })

      // resendInvite
      .addCase(resendInvite.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(resendInvite.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_invite",
          message: getResponseMessage(
            action.payload,
            "Invite sent successfully"
          )
        }
      })
      .addCase(resendInvite.rejected, (state) => {
        state.saveFetching = false
      })

      // suspendCustomer
      .addCase(suspendCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(suspendCustomer.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_suspend",
          message: getResponseMessage(
            action.payload,
            "Customer suspended successfully"
          )
        }
      })
      .addCase(suspendCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      // reactivateCustomer
      .addCase(reactivateCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(reactivateCustomer.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_unsuspend",
          message: getResponseMessage(
            action.payload,
            "Customer unsuspended successfully"
          )
        }
      })
      .addCase(reactivateCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      // inviteCustomer
      .addCase(inviteCustomer.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(inviteCustomer.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: "customer_invite",
          message: getResponseMessage(
            action.payload,
            "Invite sent successfully"
          )
        }
      })
      .addCase(inviteCustomer.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(logout.fulfilled, () => {
        console.log("customers logout")
        return initialState
      })
  }
})

/**
 * What the list's "Clear filters" writes. `customersSetProp` merges into the
 * stored filters, so a filter is cleared only by naming it; the search is left
 * alone on purpose.
 */
export const CLEARED_CUSTOMER_FILTERS = {
  status: null,
  created_after: null,
  created_before: null
}

export const { customersSetProp, clearCustomersFilters } =
  customersSlice.actions

export default customersSlice
